import { Route } from '@angular/router';
import { AdminGuard } from '@core/auth/guards/admin.guard';
import { Licence, LicenseGuardGenerator } from '@core/auth/guards/license.guard';
import { SpaceGuard } from '@core/auth/guards/space.guard';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: '/home' },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: '/home' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            //{path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes') },
            { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') }
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
            { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') }
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard, SpaceGuard("normal")],
        canActivateChild: [AuthGuard, SpaceGuard("normal")],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'dense'
        },
        children: [
            { path: 'company', loadChildren: () => import('app/modules/admin/company/company.routes') },
            { path: 'singlecompany/:id', loadChildren: () => import('app/modules/admin/singlecompany/singlecompany.routes') },
            { path: 'entities', loadChildren: () => import('app/modules/admin/entities/entities.routes') },
            { path: 'side-by-side', loadChildren: () => import('app/modules/admin/side-by-side/side-by-side.routes') },
            { path: 'favorites', loadChildren: () => import('app/modules/admin/favorites/favorites.routes') },
            { path: 'metric-lists', loadChildren: () => import('app/modules/admin/metric-lists/metric-list.routes') },
            { path: 'company-lists', loadChildren: () => import('app/modules/admin/company-lists/company-list.routes') },
            { path: 'comparison-home', loadChildren: () => import('app/modules/admin/comparison-home/comparison-home.routes') },
            { path: 'static-company-list/:id', loadChildren: () => import('app/modules/admin/static-company-list/static-company-list.routes') },
            { path: 'dynamic-company-list/:id', loadChildren: () => import('app/modules/admin/dynamic-company-list/dynamic-company-list.routes') },
            { path: 'release-notes', loadChildren: () => import('app/modules/admin/release-notes/release-notes.routes') },
            { path: 'disclosure', loadChildren: () => import('app/modules/admin/disclosure/disclosure.routes') },
            { path: 'home', loadChildren: () => import('app/modules/admin/home/home.routes') },
            { path: 'ai-sandbox', loadChildren: () => import('@app/modules/admin/ai-sandbox/ai-sandbox.routes') }

        ]
    },
    {
        path: '',
        canActivate: [AuthGuard, SpaceGuard("normal"), LicenseGuardGenerator(['benchmark', 'disclosure-benchmark', 'benchmark-view', 'notes-mapping', 'human-view', 'mapping-view', 'esrs-e1-1', 'wacc', 'useful-life', 'pensions-actuarial-assumptions', 'goodwill-benchmark', 'plan-descriptions', 'ppa-benchmark', 'disclosures-checklist'], Licence.DataAnalysis)],
        canActivateChild: [AuthGuard, SpaceGuard("normal"), LicenseGuardGenerator(['benchmark', 'disclosure-benchmark', 'benchmark-view', 'notes-mapping', 'human-view', 'mapping-view', 'esrs-e1-1', 'wacc', 'useful-life', 'pensions-actuarial-assumptions', 'goodwill-benchmark', 'plan-descriptions', 'ppa-benchmark', 'disclosures-checklist'], Licence.DataAnalysis)],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'dense'
        },
        children: [
            { path: 'benchmark', loadChildren: () => import('app/modules/admin/benchmarks/benchmark.routes') },
            { path: 'disclosure-benchmark', loadChildren: () => import('app/modules/admin/benchmarks/disclosure-benchmark/disclosure-benchmark.routes') },
            { path: 'benchmark-view', loadChildren: () => import('app/modules/admin/benchmarks/benchmark-view/benchmark-view.routes') },
            { path: 'notes-mapping', loadChildren: () => import('app/modules/admin/benchmarks/notes-mapping/notes-mapping.routes') },
            { path: 'human-view', loadChildren: () => import('app/modules/admin/benchmarks/human-view/human-view.routes') },
            { path: 'mapping-view', loadChildren: () => import('app/modules/admin/benchmarks/mapping-view/mapping-view.routes') },
            { path: 'esrs-e1-1', loadChildren: () => import('app/modules/admin/benchmarks/ESRS-E1-1/ESRS-E1-1.routes') },
            { path: 'wacc', loadChildren: () => import('app/modules/admin/benchmarks/wacc/wacc.routes') },
            { path: 'useful-life', loadChildren: () => import('app/modules/admin/benchmarks/useful-life/useful-life.routes') },
            { path: 'goodwill-benchmark', loadChildren: () => import('app/modules/admin/benchmarks/goodwill-benchmark/goodwill-benchmark.routes') },
            { path: 'pensions-actuarial-assumptions', loadChildren: () => import('app/modules/admin/benchmarks/pensions-actuarial-assumptions/pensions-actuarial-assumptions.routes') },
            { path: 'plan-descriptions', loadChildren: () => import('app/modules/admin/benchmarks/plan-descriptions/plan-descriptions.routes') },
            { path: 'ppa-benchmark', loadChildren: () => import('app/modules/admin/benchmarks/ppa-benchmark/ppa-benchmark.routes') },
            { path: 'disclosures-checklist', loadChildren: () => import('app/modules/admin/benchmarks/disclosures-checklist/disclosures-checklist.routes') },


        ]
    },
    {
        path: '',
        canActivate: [AuthGuard, SpaceGuard("normal")],
        canActivateChild: [AuthGuard, SpaceGuard("normal")],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'dense'
        },
        children: [
            { path: 'corpo-scope', loadChildren: () => import('app/modules/admin/corpo-scope/corpo-scope.routes') },
        ]
    },

    {
        path: '',
        canActivate: [AuthGuard, AdminGuard, SpaceGuard("normal")],
        canActivateChild: [AuthGuard, AdminGuard, SpaceGuard("normal")],
        component: LayoutComponent,
        data: {
            layout: 'dense'
        },
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            { path: 'admin', loadChildren: () => import('app/modules/admin/admin/admin.routes')},
            { path: 'admin-users', loadChildren: () => import('app/modules/admin/admin/admin-users/admin-users.routes') },
            { path: 'admin-metrics', loadChildren: () => import('app/modules/admin/admin/admin-metrics/admin-metrics.routes') },
            { path: 'admin-csv', loadChildren: () => import('app/modules/admin/admin/admin-csvs/admin-csvs.routes') },
            { path: 'admin-index', loadChildren: () => import('app/modules/admin/admin/admin-index/admin-index.routes') },
            { path: 'admin-data-validation', loadChildren: () => import('app/modules/admin/admin/admin-data-validation/admin-data-validation.routes') },
            { path: 'admin-upload-report', loadChildren: () => import('app/modules/admin/admin/admin-upload-report/admin-upload-report.routes') },
            { path: 'admin-report-annotation-tool', loadChildren: () => import('app/modules/admin/admin/report-annotation-tool/report-annotation-tool.routes') },
            { path: 'admin-report-annotation-management', loadChildren: () => import('app/modules/admin/admin/report-annotation-management/report-annotation-management.routes') },
            { path: 'ai-annotation', loadChildren: () => import('app/modules/admin/admin/ai-annotation/ai-annotation.routes') },
            { path: 'prompt-template', loadChildren: () => import('app/modules/admin/admin/prompt-template/prompt-template.routes') },
            { path: 'augmentation', loadChildren: () => import('app/modules/admin/admin/augmentation/augmentation.routes') },
            { path: 'augmentation-linked-facts', loadChildren: () => import('app/modules/admin/admin/augmentation-linked-facts/augmentation-linked-facts.routes') },
            { path: 'cdc-prompt-management', loadChildren: () => import('app/modules/admin/admin/cdc-prompt-definition/cdc-prompt-definition.routes') },
            { path: 'custom-issuer', loadChildren: () => import('app/modules/admin/admin/custom-issuer/custom-issuer.routes') },
            { path: 'admin-corporatings-calculation-management', loadChildren: () => import('app/modules/admin/admin/corporatings-calculation-management/corporatings-calculation-management.routes') },
            { path: 'insights-prompt-templates', loadChildren: () => import('app/modules/admin/admin/insights-prompt-templates/insights-prompt-templates.routes') },
            { path: 'insights-jobs', loadChildren: () => import('app/modules/admin/admin/insights-jobs/insights-jobs.routes') },
            { path: 'insights-insights-facts', loadChildren: () => import('app/modules/admin/admin/insights-insights-facts/insights-insights-facts.routes') },
            { path: 'auto-tagging-facts', loadChildren: () => import('app/modules/admin/admin/auto-tagging-facts/auto-tagging-facts.routes') },
            { path: 'auto-tagging-launch', loadChildren: () => import('app/modules/admin/admin/auto-tagging-launch/auto-tagging-launch.routes') },
            { path: 'auto-tagging-jobs', loadChildren: () => import('app/modules/admin/admin/auto-tagging-jobs/auto-tagging-jobs.routes') },


        ]
    },
    {
        path: '',
        canActivate: [AuthGuard, SpaceGuard("normal")],
        canActivateChild: [AuthGuard, SpaceGuard("normal")],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            { path: 'report-viewer', loadChildren: () => import('app/modules/admin/report-viewer/report-viewer.routes') },
        ]
    },

    {
        path: '',
        canActivate: [AuthGuard, SpaceGuard("CDC")],
        canActivateChild: [AuthGuard, SpaceGuard("CDC")],
        component: LayoutComponent,
        data: {
            layout: 'dense'
        },
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            //{ path: 'cdc-space', loadChildren: () => import('app/modules/admin/spaces/cdc-space/example.routes')},
            { path: 'cdc-space-home', loadChildren: () => import('app/modules/admin/cdc-studies/cdc-studies.routes')},
            { path: 'cdc-company-view/:id', loadChildren: () => import('app/modules/admin/cdc-company-view/cdc-company-view.routes') },
            { path: 'cdc-donnees-financieres/:id', loadChildren: () => import('app/modules/admin/cdc-donnees-financieres/cdc-donnees-financieres.routes') },
            { path: 'cdc-actualites/:id', loadChildren: () => import('app/modules/admin/cdc-actualites/cdc-actualites.routes') },
            { path: 'cdc-analyse-financiere/:id', loadChildren: () => import('app/modules/admin/cdc-analyse-financiere/cdc-analyse-financiere.routes') },

        ]
    },


    { path: '**', redirectTo: '/home' }
];
